import clsx from 'clsx';
import React, { useState } from 'react';

import useCurrentUser from '@hooks/useCurrentUser';
import useEventTracking from '@hooks/useEventTracking';
import { ContentType } from '@tracking';
import { Comment as CommentType, CommentableType } from '@utils/backend-api/comments';

import CommentsFeed from '../Feed';
import { DELETED_STATE } from '../constants';
import CommentActions from './CommentActions';
import CommentContent from './Content';
import CommentForm from './Form';

const MAX_COMMENT_LEVEL = 2;

export default function Comment({
  comment,
  commentableType,
  level = 1,
  setParentReplyingTo,
}: {
  comment: CommentType;
  commentableType: CommentableType;
  level?: number;
  setParentReplyingTo?: (parentReplyingTo: string) => void;
}) {
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const isReplyComment = level > 1;
  const { currentUser } = useCurrentUser();
  const { Track } = useEventTracking({
    creatorIds: [comment.user.id],
    contentType: ContentType.Comment,
    contentId: comment.id,
  });

  // Clicking reply on a MAX_COMMENT_LEVEL comment shows the form for its parent comment
  const replyToParent = level >= MAX_COMMENT_LEVEL;
  const commentableId = replyToParent ? comment.commentable_id : comment.id;
  const setReplyingToFormFunc = replyToParent ? setParentReplyingTo : setReplyingTo;
  const onEditComment = () => setIsEditing(true);
  const hideEditForm = () => setIsEditing(false);

  return (
    <Track>
      <div className="comment-container">
        <div
          className={clsx('comment-single', {
            'ml-[55px] sm:ml-64': isReplyComment,
          })}
          id={`comment-${comment.id}`}
        >
          <div className="comment-main">
            {isEditing ? (
              <div className="comment-body">
                <CommentForm
                  comment={comment}
                  hideEditForm={hideEditForm}
                  commentableId={commentableId}
                  commentableType={CommentableType.Comment}
                />
              </div>
            ) : (
              <CommentContent comment={comment} />
            )}
            {comment.state !== DELETED_STATE && (
              <CommentActions
                comment={comment}
                onEditComment={onEditComment}
                setReplyingTo={setReplyingToFormFunc}
              />
            )}
          </div>
        </div>
        {comment.child_comments && (
          <CommentsFeed
            comments={comment.child_comments}
            setParentReplyingTo={setReplyingTo}
            level={level + 1}
            commentableType={commentableType}
          />
        )}
        {level < MAX_COMMENT_LEVEL && replyingTo && currentUser?.permitted_to_comment && (
          <CommentForm
            commentableId={commentableId}
            commentableType={CommentableType.Comment}
            hideReplyForm={() => setReplyingTo(null)}
            replyingTo={replyingTo}
          />
        )}
      </div>
    </Track>
  );
}
